import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { invitesApi } from './invites'
import { queryKeys } from './query-keys'
import type { AcceptInviteResponse } from './types'

/** Public invite preview — no auth required.
 *
 * Suppressed-retry status codes:
 *  - 404 — uuid doesn't exist
 *  - 410 — invite was active but is now revoked / expired (semantic "gone")
 *  - 403 — the real BE returns this for already-accepted invites (observed in staging),
 *          even though the public route is unauthenticated. Treat it as "no longer valid"
 *          for UX purposes; retrying won't change the outcome.
 */
export function useInvite(uuid: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.invites.byUuid(uuid ?? 'none'),
    queryFn: () => invitesApi.get(String(uuid)),
    enabled: !!uuid,
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status
      if (status === 404 || status === 410 || status === 403) return false
      return failureCount < 2
    },
  })
}

/** Public accept — uuid is the bearer token; the backend uses the invite's bound user. */
export function useAcceptInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string): Promise<AcceptInviteResponse> => invitesApi.accept(uuid),
    onSuccess: (_, uuid) => {
      qc.invalidateQueries({ queryKey: queryKeys.teams.all })
      qc.invalidateQueries({ queryKey: queryKeys.invites.byUuid(uuid) })
    },
  })
}
